import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs, addDoc, deleteDoc, updateDoc, Timestamp, writeBatch, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import QrScanner from 'react-qr-scanner'; // Assuming this library is installed

import QRScanDetailAlat from './QRScanDetailAlat';
import QRScanActionButtons from './QRScanActionButtons';
import QRScanSOP from './QRScanSOP';

// Basic styling (can be moved to a CSS file later)
const styles = {
  container: { padding: '2rem', fontFamily: 'Arial, sans-serif' },
  scannerContainer: { width: '300px', margin: '20px auto', border: '1px solid #ccc' },
  loading: { textAlign: 'center', fontSize: '1.2rem', color: '#555', marginTop: '2rem' },
  error: { color: 'red', textAlign: 'center', marginTop: '1rem', fontWeight: 'bold' },
  info: { textAlign: 'center', marginTop: '1rem', color: '#333' },
  resetButton: {
    display: 'block',
    margin: '1rem auto',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
  }
};

const QRScanPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentUser, setCurrentUser] = useState(null);
  const [scanResult, setScanResult] = useState(null); // Stores the scanned tool ID
  const [scannedAlatData, setScannedAlatData] = useState(null); // Stores details of the scanned tool
  const [isCurrentlyUsing, setIsCurrentlyUsing] = useState(false); // Is the current user using this specific tool?
  const [currentUsageDocId, setCurrentUsageDocId] = useState(null); // Doc ID in active_instrument_usage
  const [logbookDocId, setLogbookDocId] = useState(null); // Doc ID for the ongoing logbook entry
  const isCompletingFromStuckScreen = searchParams.get('complete') === 'true'; // Check if coming from stuck screen
  // Only show scanner initially if NOT completing from stuck screen
  const [showScanner, setShowScanner] = useState(!isCompletingFromStuckScreen);
  const [showSOP, setShowSOP] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activityCategory, setActivityCategory] = useState('Praktikum'); // Kategori: Praktikum atau Penelitian
  const [activityDescription, setActivityDescription] = useState(''); // Deskripsi kegiatan (kalimat aktif)
  const scannerRef = useRef(null);
  const autoCompleteTimeoutRef = useRef(null); // Store timeout ID for auto-complete

  // 1. Get Current User
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setCurrentUser({ uid: user.uid, ...userSnap.data() });
        } else {
          setError('Data pengguna tidak ditemukan.');
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
        setError('Silakan login untuk menggunakan fitur scan.');
      }
      setLoading(false); // Initial user loading done
    });
    return () => unsubscribe();
  }, []);

  // 2. Handle Scan Result
  const handleScan = (data) => {
    if (data && data.text) {
      console.log('Scanned:', data.text);
      setError(''); // Clear previous errors
      setScanResult(data.text); // Assume the QR code contains the tool ID directly
      setShowScanner(false);
      setLoading(true); // Start loading tool data
    }
  };

  const handleScanError = (err) => {
    console.error('Scan Error:', err);
    // Avoid flooding with common errors, maybe show a persistent one
    if (!error) {
        setError('Gagal memindai QR code. Pastikan kamera berfungsi dan ada izin.');
    }
  };

  // Handle completion from stuck screen - auto-process the alatId if provided
  useEffect(() => {
    if (isCompletingFromStuckScreen) {
      // Always disable scanner when completing from stuck screen
      if (showScanner) {
        console.log('Disabling scanner because completing from stuck screen');
        setShowScanner(false);
      }
      
      if (currentUser && !loading) {
        const alatId = searchParams.get('alatId');
        if (alatId && !scanResult) {
          // Auto-process the alatId as if it was scanned
          console.log('Auto-processing alatId from stuck screen:', alatId);
          setScanResult(alatId);
          setShowScanner(false); // Ensure scanner is off when completing
          setLoading(true);
        }
      }
    }
  }, [isCompletingFromStuckScreen, currentUser, searchParams, loading, scanResult, showScanner]);

  // Helper function to check if logbook is cross-day (start date is different from today)
  const isCrossDay = (tanggalMulai) => {
    const today = new Date();
    const todayString = today.toISOString().split('T')[0]; // YYYY-MM-DD
    return tanggalMulai !== todayString;
  };

  // Helper function to check if logbook should be auto-completed
  const shouldAutoComplete = (logbookData) => {
    if (!logbookData.tanggalMulai || !logbookData.jamMulai) return false;
    
    // Check cross-day: if start date is different from today, auto-complete
    if (isCrossDay(logbookData.tanggalMulai)) {
      return true;
    }
    
    // Check if it's been more than 1 hour since start
    const startDate = new Date(`${logbookData.tanggalMulai}T${logbookData.jamMulai}:00`);
    const now = new Date();
    const hoursElapsed = (now - startDate) / (1000 * 60 * 60); // Convert to hours
    
    return hoursElapsed >= 1;
  };

  // Helper function to auto-complete logbook (with condition "Baik" when forced by another user)
  const autoCompleteLogbook = async (logbookId, usageDocId, alatData, userId, forceComplete = false) => {
    try {
      const logbookRef = doc(db, 'logbook_instrumen', logbookId);
      const logbookSnap = await getDoc(logbookRef);
      
      if (!logbookSnap.exists()) {
        console.log('Logbook not found for auto-complete');
        return;
      }

      const logbookData = logbookSnap.data();
      
      // Check if already completed
      if (logbookData.tanggalSelesai && logbookData.jamSelesai) {
        console.log('Logbook already completed');
        return;
      }

      const batch = writeBatch(db);

      // Calculate end time
      let endTime;
      if (forceComplete) {
        // If forced by another user, use current time as end time
        endTime = new Date();
      } else {
        // Normal auto-complete: 1 hour after start time
        const startDate = new Date(`${logbookData.tanggalMulai}T${logbookData.jamMulai}:00`);
        endTime = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour
      }
      
      const endDateString = endTime.toISOString().split('T')[0];
      const endTimeString = endTime.toTimeString().split(' ')[0].substring(0, 5);

      // If forced by another user, always use "Baik" as conditionAfter
      // Otherwise, use conditionBefore as conditionAfter (assume same condition)
      const conditionAfter = forceComplete ? 'Baik' : (logbookData.conditionBefore || 'Baik');
      
      batch.update(logbookRef, {
        conditionAfter: conditionAfter,
        tanggalSelesai: endDateString,
        jamSelesai: endTimeString,
        logTimestamp: serverTimestamp(),
        autoCompleted: true, // Flag to indicate this was auto-completed
        forcedByAnotherUser: forceComplete, // Flag to indicate if forced by another user
      });

      // Delete active usage record
      if (usageDocId) {
        const activeUsageRef = doc(db, 'active_instrument_usage', usageDocId);
        batch.delete(activeUsageRef);
      }

      // Update alat status to available
      const alatDocRef = doc(db, alatData.type === 'instrumen' ? 'instrumen' : 'alat_gelas', alatData.id);
      batch.update(alatDocRef, { statusAlat: 'tersedia' });

      await batch.commit();
      console.log(`Logbook auto-completed${forceComplete ? ' (forced by another user)' : ' after 1 hour'}`);

    } catch (err) {
      console.error('Error auto-completing logbook:', err);
      throw err; // Re-throw to handle in calling function
    }
  };

  // 3. Process Scan Result: Fetch Tool Data & Check Usage Status
  useEffect(() => {
    if (!scanResult || !currentUser) return;

    const fetchToolAndStatus = async () => {
      setLoading(true);
      setError('');
      setScannedAlatData(null);
      setIsCurrentlyUsing(false);
      setCurrentUsageDocId(null);
      setLogbookDocId(null);
      let hasValidRequest = false; // Flag to check borrowing request

      try {
        // --- Check for Active Approved Borrowing Request ---
        // Fixed: Check for any active loan (not just today's), including loans from previous days that haven't been returned
        const today = new Date();
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()); // Start of today

        const requestQuery = query(
          collection(db, 'permintaan_alat'),
          where('userId', '==', currentUser.uid),
          where('alatId', '==', scanResult),
          where('status', 'in', ['approved', 'admin_approved']) // Include both approved statuses
        );
        const requestSnapshot = await getDocs(requestQuery);

        requestSnapshot.forEach((reqDoc) => {
            const reqData = reqDoc.data();
            const loanEnd = reqData.tanggalKembali?.toDate();
            const loanStart = reqData.tanggalPinjam?.toDate();
            
            if (!loanEnd || !loanStart) {
              return; // Skip if dates are missing
            }

            // Check if loan is still active (not fully returned)
            const totalBorrowed = reqData.jumlah || 0;
            const alreadyReturned = reqData.jumlahSudahDikembalikan || 0;
            const isFullyReturned = alreadyReturned >= totalBorrowed || reqData.status === 'returned';
            
            // Loan is valid if:
            // 1. Loan period hasn't ended yet (tanggalKembali >= todayStart)
            // 2. OR loan period has ended but not fully returned (user forgot to return)
            // This allows users to complete/return items even if they forgot on the return date
            if (loanEnd >= todayStart || !isFullyReturned) {
                hasValidRequest = true;
                console.log("Valid active approved request found:", reqDoc.id, {
                  loanStart: loanStart.toLocaleDateString('id-ID'),
                  loanEnd: loanEnd.toLocaleDateString('id-ID'),
                  isFullyReturned,
                  alreadyReturned,
                  totalBorrowed
                });
            }
        });

        if (!hasValidRequest) {
             throw new Error(`Anda tidak memiliki peminjaman aktif yang disetujui untuk alat ini. Pastikan Anda telah meminjam alat ini dan belum mengembalikannya sepenuhnya.`);
        }
        // --- End Borrowing Request Check ---


        // Fetch tool details (try instrumen first, then alat_gelas)
        let alatDocRef = doc(db, 'instrumen', scanResult);
        let alatSnap = await getDoc(alatDocRef);
        let alatData = null;
        let alatType = 'instrumen';

        if (!alatSnap.exists()) {
          alatDocRef = doc(db, 'alat_gelas', scanResult);
          alatSnap = await getDoc(alatDocRef);
          alatType = 'alat_gelas';
        }

        if (!alatSnap.exists()) {
          throw new Error(`Alat dengan ID ${scanResult} tidak ditemukan.`);
        }
        alatData = { id: alatSnap.id, type: alatType, ...alatSnap.data() };
        setScannedAlatData(alatData);

        // Check active usage status for THIS tool
        const usageQuery = query(collection(db, 'active_instrument_usage'), where('alatId', '==', scanResult));
        const usageSnapshot = await getDocs(usageQuery);

        if (!usageSnapshot.empty) {
          const usageDoc = usageSnapshot.docs[0]; // Should only be one active usage per tool
          const usageData = usageDoc.data();
          
          // Check if the request has been returned - if yes, clean up active_instrument_usage
          const requestCheckQuery = query(
            collection(db, 'permintaan_alat'),
            where('userId', '==', usageData.userId),
            where('alatId', '==', scanResult),
            where('status', '==', 'returned')
          );
          const requestCheckSnapshot = await getDocs(requestCheckQuery);
          
          if (!requestCheckSnapshot.empty) {
            // Request has been returned, but active_instrument_usage still exists - clean it up
            await deleteDoc(usageDoc.ref);
            console.log('Cleaned up active_instrument_usage for returned request');
            setIsCurrentlyUsing(false);
          } else {
            // Check if the logbook entry is actually completed
            if (usageData.logbookEntryId) {
              const logbookRef = doc(db, 'logbook_instrumen', usageData.logbookEntryId);
              const logbookSnap = await getDoc(logbookRef);
              
              if (logbookSnap.exists()) {
                const logbookData = logbookSnap.data();
                // If logbook is incomplete (no tanggalSelesai), it means previous user didn't finish
                const isLogbookIncomplete = !logbookData.tanggalSelesai || !logbookData.jamSelesai;
                
                if (isLogbookIncomplete) {
                  // ALWAYS check if should auto-complete first (cross-day or >= 1 hour) - FALLBACK CLEANUP
                  if (shouldAutoComplete(logbookData)) {
                    // Auto-complete the logbook (cross-day or >= 1 hour)
                    console.log('Auto-completing logbook due to cross-day or >= 1 hour');
                    await autoCompleteLogbook(usageData.logbookEntryId, usageDoc.id, alatData, usageData.userId, false);
                    
                    // After auto-complete, allow current user to proceed
                    setIsCurrentlyUsing(false);
                  } else {
                    // Less than 1 hour and same day, handle based on user
          if (usageData.userId === currentUser.uid) {
                      // Same user - allow them to continue
            setIsCurrentlyUsing(true);
            setCurrentUsageDocId(usageDoc.id);
                      setLogbookDocId(usageData.logbookEntryId);
                      
                      // Set timeout to auto-complete after remaining time
                      const startDate = new Date(`${logbookData.tanggalMulai}T${logbookData.jamMulai}:00`);
                      const now = new Date();
                      const remainingTime = (1 * 60 * 60 * 1000) - (now - startDate); // Remaining milliseconds
                      if (remainingTime > 0) {
                        if (autoCompleteTimeoutRef.current) {
                          clearTimeout(autoCompleteTimeoutRef.current);
                        }
                        autoCompleteTimeoutRef.current = setTimeout(() => {
                          autoCompleteLogbook(usageData.logbookEntryId, usageDoc.id, alatData, currentUser.uid, false);
                        }, remainingTime);
                      }
                    } else {
                      // Different user - auto-complete previous user's logbook with "Baik" condition
                      // This allows the new user to scan immediately (even if < 1 hour)
                      console.log(`Auto-completing previous user's logbook to allow new user to scan`);
                      await autoCompleteLogbook(usageData.logbookEntryId, usageDoc.id, alatData, usageData.userId, true);
                      
                      // After auto-completing previous user's logbook, allow current user to proceed
                      setIsCurrentlyUsing(false);
                    }
                  }
                } else {
                  // Logbook is complete but active_instrument_usage still exists (shouldn't happen, but handle it)
                  // Clean up the orphaned active usage entry - FALLBACK CLEANUP
                  await deleteDoc(usageDoc.ref);
                  console.log('Cleaned up orphaned active usage entry (logbook already complete)');
                  setIsCurrentlyUsing(false);
                }
              } else {
                // Logbook entry doesn't exist, clean up orphaned active usage - FALLBACK CLEANUP
                await deleteDoc(usageDoc.ref);
                console.log('Cleaned up orphaned active usage entry (logbook not found)');
                setIsCurrentlyUsing(false);
              }
          } else {
              // No logbook entry ID, this is an orphaned active usage entry - FALLBACK CLEANUP
              await deleteDoc(usageDoc.ref);
              console.log('Cleaned up orphaned active usage entry (no logbook ID)');
              setIsCurrentlyUsing(false);
            }
          }
        } else {
          // Tool is not currently in active use
          setIsCurrentlyUsing(false);
        }

      } catch (err) {
        console.error("Error fetching tool/status:", err);
        setError(err.message);
        setScanResult(null); // Reset scan on error to allow re-scan
        // Only show scanner if NOT completing from stuck screen
        if (!isCompletingFromStuckScreen) {
        setShowScanner(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchToolAndStatus();
  }, [scanResult, currentUser]);

  // 4. Handle Start Usage
  const handleStartUsage = async (startCondition) => { // No need to change signature
    if (!currentUser || !scannedAlatData || !activityCategory) { // Check if activity category is selected
        setError('Silakan pilih kategori kegiatan (Praktikum/Penelitian).');
        return;
    }
    if (!activityDescription || activityDescription.trim() === '') { // Check if activity description is filled
        setError('Silakan isi deskripsi kegiatan (gunakan kalimat aktif).');
        return;
    }
    setLoading(true);
    setError('');

    const batch = writeBatch(db);

    try {
      const startTime = new Date(); // Use JS Date object to extract parts
      const startDateString = startTime.toISOString().split('T')[0]; // YYYY-MM-DD
      const startTimeString = startTime.toTimeString().split(' ')[0].substring(0, 5); // HH:MM

      // a. Create initial logbook entry in the correct collection with matching fields
      const logbookRef = doc(collection(db, 'logbook_instrumen'));
      batch.set(logbookRef, {
        userId: currentUser.uid,
        userName: currentUser.nama || currentUser.email, // Match field name used in report/manual form
        userNim: currentUser.role === 'mahasiswa' ? (currentUser.nim || 'N/A') : 'N/A',
        userNip: currentUser.role === 'dosen' ? (currentUser.nip || 'N/A') : 'N/A', // Add NIP for dosen
        userProgramStudi: currentUser.role === 'mahasiswa' ? (currentUser.programStudi || 'N/A') : 'N/A',
        instrumentId: scannedAlatData.id, // Match field name
        instrumentName: scannedAlatData.nama, // Match field name
        // jenisAlat: scannedAlatData.type, // This field might not be in logbook_instrumen, check report if needed
        activity: activityCategory, // Kategori kegiatan (Praktikum/Penelitian)
        activityDescription: activityDescription.trim(), // Deskripsi kegiatan (kalimat aktif)
        conditionBefore: startCondition, // Match field name
        conditionAfter: null, // Initial state
        tanggalMulai: startDateString, // Save date string
        jamMulai: startTimeString, // Save time string
        tanggalSelesai: null, // Initial state
        jamSelesai: null, // Initial state
        logTimestamp: serverTimestamp(), // Keep Firestore server timestamp for ordering/audit
        // status: 'ongoing', // Status field might not be used in logbook_instrumen, check report if needed
      });

      // b. Create active usage record, linking to logbook entry
      const activeUsageRef = doc(collection(db, 'active_instrument_usage')); // Auto-generate ID
      batch.set(activeUsageRef, {
        userId: currentUser.uid,
        alatId: scannedAlatData.id,
        startTime: startTime,
        startCondition: startCondition,
        logbookEntryId: logbookRef.id, // Link to the logbook document
      });

      // c. Handle damage report if needed
      if (startCondition === 'Rusak') {
        const damageReportRef = doc(collection(db, 'damage_reports'));
        batch.set(damageReportRef, {
          alatId: scannedAlatData.id,
          namaAlat: scannedAlatData.nama,
          jenisAlat: scannedAlatData.type,
          reportedByUserId: currentUser.uid,
          reportedByUserName: currentUser.nama || currentUser.email,
          reportTime: startTime,
          description: `Dilaporkan rusak saat mulai penggunaan via QR Scan.`,
          conditionReported: 'Rusak',
          status: 'pending', // Laboran/Admin needs to verify
        });
        // Optionally update the instrument status itself
        const alatDocRef = doc(db, scannedAlatData.type === 'instrumen' ? 'instrumen' : 'alat_gelas', scannedAlatData.id);
        batch.update(alatDocRef, { statusAlat: 'rusak' }); // Assuming 'statusAlat' field exists
      }

      console.log("Attempting to commit batch for starting usage...");
      await batch.commit();
      console.log("Batch commit successful for starting usage.");

      // Update state
      setIsCurrentlyUsing(true);
      setCurrentUsageDocId(activeUsageRef.id); // Store the ID of the active usage doc
      setLogbookDocId(logbookRef.id); // Store the ID of the logbook doc

      // Set timeout to auto-complete after 1 hour
      if (autoCompleteTimeoutRef.current) {
        clearTimeout(autoCompleteTimeoutRef.current);
      }
      autoCompleteTimeoutRef.current = setTimeout(() => {
        autoCompleteLogbook(logbookRef.id, activeUsageRef.id, scannedAlatData, currentUser.uid);
        // Reset state after auto-complete
        setIsCurrentlyUsing(false);
        setCurrentUsageDocId(null);
        setLogbookDocId(null);
      }, 60 * 60 * 1000); // 1 hour in milliseconds

      // After starting usage, navigate to dashboard to show all active usages
      // Reset scan state first
      setScanResult(null);
      setScannedAlatData(null);
      setActivityCategory('Praktikum'); // Reset to default
      setActivityDescription(''); // Reset activity description
      setShowScanner(false); // Don't show scanner, we're navigating away
      
      alert('Penggunaan alat berhasil dimulai. Anda akan diarahkan ke dashboard untuk melihat semua alat yang sedang digunakan.');
      
      // Small delay to ensure state is reset before navigation
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 100);

    } catch (err) {
      console.error("Error starting usage:", err);
      setError(`Gagal memulai penggunaan: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 5. Handle End Usage
  const handleEndUsage = async (endCondition) => {
    if (!currentUser || !scannedAlatData || !currentUsageDocId || !logbookDocId) return;
    setLoading(true);
    setError('');

    const batch = writeBatch(db);

    try {
      const endTime = new Date(); // Use JS Date object to extract parts
      const endDateString = endTime.toISOString().split('T')[0]; // YYYY-MM-DD
      const endTimeString = endTime.toTimeString().split(' ')[0].substring(0, 5); // HH:MM

      // a. Update logbook entry in the correct collection with matching fields
      const logbookRef = doc(db, 'logbook_instrumen', logbookDocId);
      batch.update(logbookRef, {
        conditionAfter: endCondition, // Match field name
        tanggalSelesai: endDateString, // Save date string
        jamSelesai: endTimeString, // Save time string
        // status: 'completed', // Status field might not be used in logbook_instrumen
        logTimestamp: serverTimestamp() // Update timestamp on completion as well
      });

      // b. Delete active usage record
      const activeUsageRef = doc(db, 'active_instrument_usage', currentUsageDocId);
      batch.delete(activeUsageRef);

      // c. Handle damage report if needed
      if (endCondition === 'Rusak') {
        const damageReportRef = doc(collection(db, 'damage_reports'));
        batch.set(damageReportRef, {
          alatId: scannedAlatData.id,
          namaAlat: scannedAlatData.nama,
          jenisAlat: scannedAlatData.type,
          reportedByUserId: currentUser.uid,
          reportedByUserName: currentUser.nama || currentUser.email,
          reportTime: endTime,
          description: `Dilaporkan rusak saat selesai penggunaan via QR Scan.`,
          conditionReported: 'Rusak',
          status: 'pending',
        });
        // Optionally update the instrument status
        const alatDocRef = doc(db, scannedAlatData.type === 'instrumen' ? 'instrumen' : 'alat_gelas', scannedAlatData.id);
        batch.update(alatDocRef, { statusAlat: 'rusak' });
      } else {
         // If ended in good condition, ensure status is 'tersedia' or similar
         const alatDocRef = doc(db, scannedAlatData.type === 'instrumen' ? 'instrumen' : 'alat_gelas', scannedAlatData.id);
         // Only update if it was previously marked 'rusak' maybe? Or just set to 'tersedia'.
         // Let's assume we just ensure it's available if reported 'Baik'.
         batch.update(alatDocRef, { statusAlat: 'tersedia' });
      }

      console.log("Attempting to commit batch for ending usage...");
      await batch.commit();
      console.log("Batch commit successful for ending usage.");

      // Clear auto-complete timeout since user completed manually
      if (autoCompleteTimeoutRef.current) {
        clearTimeout(autoCompleteTimeoutRef.current);
        autoCompleteTimeoutRef.current = null;
      }

      // Reset state after successful completion
      setScanResult(null);
      setScannedAlatData(null);
      setIsCurrentlyUsing(false);
      setCurrentUsageDocId(null);
      setLogbookDocId(null);
      setActivityCategory('Praktikum'); // Reset to default
      setActivityDescription(''); // Reset activity description
      setShowScanner(false); // Don't show scanner, we're navigating to dashboard
      
      // Always navigate to dashboard after completing usage
      // Check if there are still other active usages (for informational purposes)
      const remainingUsageQuery = query(
        collection(db, 'active_instrument_usage'),
        where('userId', '==', currentUser.uid)
      );
      const remainingUsageSnapshot = await getDocs(remainingUsageQuery);
      
      if (remainingUsageSnapshot.empty) {
        // No more active usages
        alert('Penggunaan alat berhasil diselesaikan.');
      } else {
        // Still have other active usages (ActiveUsageScreen will show them)
        alert('Penggunaan alat berhasil diselesaikan.');
      }
      
      // Always navigate to dashboard after completion
      navigate('/dashboard', { replace: true });

    } catch (err) {
      console.error("Error ending usage:", err);
      setError(`Gagal menyelesaikan penggunaan: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 6. Toggle SOP Visibility
  const toggleSOP = () => {
    setShowSOP(!showSOP);
  };

  // 7. Reset Scan
  const resetScan = () => {
    // Clear auto-complete timeout when resetting
    if (autoCompleteTimeoutRef.current) {
      clearTimeout(autoCompleteTimeoutRef.current);
      autoCompleteTimeoutRef.current = null;
    }
    setScanResult(null);
    setScannedAlatData(null);
    setIsCurrentlyUsing(false);
    setCurrentUsageDocId(null);
    setLogbookDocId(null);
    setActivityCategory('Praktikum'); // Reset to default
    setActivityDescription(''); // Reset activity description
    // Only show scanner if NOT completing from stuck screen
    if (!isCompletingFromStuckScreen) {
    setShowScanner(true);
    }
    setShowSOP(false);
    setError('');
    setLoading(false); // Ensure loading is false
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoCompleteTimeoutRef.current) {
        clearTimeout(autoCompleteTimeoutRef.current);
      }
    };
  }, []);

  // --- Render Logic ---
  if (loading && !scannedAlatData) { // Show initial loading or loading after scan start
    return <div style={styles.loading}>Memuat...</div>;
  }

  if (!currentUser && !loading) {
      return <div style={styles.error}>Anda harus login untuk menggunakan fitur ini.</div>
  }

  return (
    <div style={styles.container}>
      <h2>Scan Alat untuk Penggunaan</h2>

      {error && <p style={styles.error}>{error}</p>}

      {showScanner && !scanResult && !isCompletingFromStuckScreen && (
        <div style={styles.scannerContainer}>
          <p style={styles.info}>Arahkan kamera ke QR code alat.</p>
          <QrScanner
            ref={scannerRef}
            delay={300}
            onError={handleScanError}
            onScan={handleScan}
            style={{ width: '100%' }}
            constraints={{ video: { facingMode: "environment" } }} // Use back camera
          />
        </div>
      )}
      
      {/* Show message if completing from stuck screen and scanner would be active */}
      {isCompletingFromStuckScreen && showScanner && !scanResult && (
        <div style={styles.info}>
          Memproses penyelesaian penggunaan alat...
        </div>
      )}

      {loading && scannedAlatData && <div style={styles.loading}>Memproses...</div>}

      {!showScanner && scannedAlatData && !loading && (
        <div>
          <QRScanDetailAlat alatData={scannedAlatData} />

          {/* Add Activity Category and Description */}
          {!isCurrentlyUsing && (
            <div style={{ margin: '1rem 0', padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Kategori Kegiatan:
                </label>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="activityCategory"
                      value="Praktikum"
                      checked={activityCategory === 'Praktikum'}
                      onChange={(e) => setActivityCategory(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>Praktikum</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="activityCategory"
                      value="Penelitian"
                      checked={activityCategory === 'Penelitian'}
                      onChange={(e) => setActivityCategory(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>Penelitian</span>
                  </label>
                </div>
              </div>
              <div>
                <label htmlFor="activity-description" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Kegiatan: <span style={{ fontSize: '0.85rem', color: '#666', fontWeight: 'normal' }}>(Gunakan kalimat aktif)</span>
                </label>
                <input
                  type="text"
                  id="activity-description"
                  value={activityDescription}
                  onChange={(e) => setActivityDescription(e.target.value)}
                  placeholder="Contoh: Menimbang Niacinamide, Carbopol, dan Methyl Paraben"
                  style={{ 
                    width: '100%', 
                    padding: '0.75rem', 
                    fontSize: '1rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
                  Contoh: Menimbang Niacinamide, Carbopol, dan Methyl Paraben
                </div>
              </div>
            </div>
          )}

          <QRScanActionButtons
            isUsing={isCurrentlyUsing}
            onStartUsage={handleStartUsage}
            onEndUsage={handleEndUsage}
            onShowSOP={toggleSOP}
            // Disable start button if loading OR if no valid request was found (though error should prevent reaching here)
            loading={loading}
          />

          {/* Pass sopBase64 and sopFileName instead of sopUrl */}
          {showSOP && <QRScanSOP sopBase64={scannedAlatData.sopBase64} sopFileName={scannedAlatData.sopFileName} />}

          <button onClick={resetScan} style={styles.resetButton}>Scan Alat Lain</button>
        </div>
      )}
    </div>
  );
};

export default QRScanPage;
